import React, { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { searchCustomersByIdPrefix } from '../utils/db/customers';

/**
 * Props:
 * - value: currently selected customerId (uuid)
 * - valueLabel: optional display text to show when a selection is already made
 * - onChange(customer): called with selected customer row (id, customer_id, customer_name, ...)
 * - placeholder: input placeholder
 * - className: input class
 * - label: optional label text
 * - error: optional error string
 */
const CustomerAutocomplete = ({ value, valueLabel, onChange, placeholder = 'Start typing Customer ID', className = '', label, error }) => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reflect externally provided label (e.g., persisted selection)
  useEffect(() => {
    if (valueLabel && typeof valueLabel === 'string') {
      setQuery(valueLabel);
    }
  }, [valueLabel]);

  const runSearch = async (text) => {
    if (!user || !text || text.trim().length === 0) {
      setResults([]);
      return;
    }
    try {
      setLoading(true);
      const rows = await searchCustomersByIdPrefix(user.id, text.trim());
      setResults(rows);
      setOpen(true);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e) => {
    const text = e.target.value;
    setQuery(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(text), 200);
  };

  const handleSelect = (customer) => {
    setQuery(`${customer.customer_id} - ${customer.customer_name}`);
    setOpen(false);
    onChange && onChange(customer);
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      )}
      <div className="relative">
        <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => query && results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className={`w-full pl-9 pr-3 py-2 border ${error ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${className}`}
        />
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
          ) : (
            results.length > 0 ? (
              results.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleSelect(c)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                >
                  <div className="font-medium">{c.customer_id}</div>
                  <div className="text-gray-600">{c.customer_name}</div>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerAutocomplete;


